import { Grid, Typography, TextField, Checkbox, Radio, RadioGroup, FormControlLabel } from "@mui/material";
import React from "react";
import { evalDisableRules } from "../components/FormScripts";
import { isFormLineRenderedVisible, useFormClientOptions } from "../FormClientContext";
import { FormLine } from "./FormLine";

interface AssessmentProps {
	fl: FormLine;
}

export interface TestQuestion {
	caption: string;
	details_type_code: string;
	is_group: boolean;
	test_setup_details_id: string;
	output_code?: string;
	disable_rule?: string;
	is_erase_on_disable?: boolean;
	isDateActive?: boolean;
	isDisabled?: boolean;
	was_required?: boolean;
	Answers: Array<TestAnswer>;
	GetValue: Function;
	setDisabled?: (disabled: boolean) => void;
	getDisabled?: () => boolean;
	is_required: boolean;
	test_details_id: string;
}

export interface TestAnswer {
	test_setup_answers_caption: string;
	test_setup_details_answers_id: string;
	value: any;
	display_exp?: any;
	test_details_id: string;
	test_details_answers_id: string;
}

function getInitialRadio(q: TestQuestion) {
	const selected = q.Answers.find((a) => a.value === true);
	return selected?.test_setup_details_answers_id || "";
}

function getInitialMulti(q: TestQuestion) {
	const obj: Record<string, string> = {};
	q.Answers.forEach((a) => {
		if (a.value === true) obj[a.test_setup_details_answers_id] = "";
	});
	return obj;
}

function syncAnswersToQuestion(q: TestQuestion, narValue: any, radioValue: string, multiValue: Record<string, string>, numValue: any) {
	switch (q.details_type_code) {
		case "NARRATIVE":
			if (q.Answers[0]) q.Answers[0].value = narValue;
			break;
		case "NUMERIC_SCORE":
			if (q.Answers[0]) q.Answers[0].value = numValue;
			break;
		case "SINGLELIST":
			q.Answers.forEach((a) => {
				a.value = a.test_setup_details_answers_id === radioValue;
			});
			break;
		case "MULTILIST":
			q.Answers.forEach((a) => {
				a.value = multiValue.hasOwnProperty(a.test_setup_details_answers_id);
			});
			break;
	}
}

function clearQuestionState(
	q: TestQuestion,
	setNarValue: (v: any) => void,
	setRadioValue: (v: string) => void,
	setMultiValue: (v: Record<string, string>) => void,
	setNumValue: (v: any) => void
) {
	switch (q.details_type_code) {
		case "NARRATIVE":
			setNarValue("");
			if (q.Answers[0]) q.Answers[0].value = null;
			break;
		case "NUMERIC_SCORE":
			setNumValue("");
			if (q.Answers[0]) q.Answers[0].value = null;
			break;
		case "SINGLELIST":
			setRadioValue("");
			q.Answers.forEach((a) => {
				a.value = null;
			});
			break;
		case "MULTILIST":
			setMultiValue({});
			q.Answers.forEach((a) => {
				a.value = null;
			});
			break;
	}
}

export function AssessmentRender(props: AssessmentProps) {
	const questions = props.fl.testValue.Questions;

	React.useEffect(() => {
		evalDisableRules();
	}, []);

	return (
		<>
			{questions.map((x: TestQuestion, idx: number) => {
				if (x.is_group) {
					return (
						<Grid item key={idx} xs={12} sx={{ borderBottom: "1px solid #ebebeb", paddingLeft: 1, paddingTop: 1, paddingBottom: 1, marginBottom: 2 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: "600" }}>
								{x.caption}
							</Typography>
						</Grid>
					);
				}

				return <QuestionRender q={x} key={idx} isModifiable={props.fl.isModifiable}></QuestionRender>;
			})}
		</>
	);
}

interface QuestionProps {
	q: TestQuestion;
	isModifiable: boolean;
}

function QuestionRender(props: QuestionProps) {
	const x = props.q;
	const formClientOptions = useFormClientOptions();
	const modified = false;
	const [disabled, setDisabled] = React.useState(x.isDisabled || false);
	const [narValue, setNarValue] = React.useState(x.Answers[0]?.value ?? "");
	const [radioValue, setRadioValue] = React.useState(getInitialRadio(x));
	const [multiValue, setMultiValue] = React.useState(getInitialMulti(x));
	const [numValue, setNumValue] = React.useState(x.Answers[0]?.value ?? "");

	const onAnswerChange = React.useCallback(
		(nar: any, radio: string, multi: Record<string, string>, num: any) => {
			syncAnswersToQuestion(x, nar, radio, multi, num);
			evalDisableRules();
		},
		[x]
	);

	x.GetValue = () => {
		switch (x.details_type_code) {
			case "NARRATIVE":
				return narValue;
			case "SINGLELIST":
				return radioValue;
			case "MULTILIST":
				return multiValue;
			case "NUMERIC_SCORE":
				return numValue;
		}
	};

	x.getDisabled = () => disabled;

	x.setDisabled = (isFieldDisabled: boolean) => {
		const wasDisabled = x.isDisabled;
		setDisabled(isFieldDisabled);
		x.isDisabled = isFieldDisabled;

		if (isFieldDisabled) {
			if (x.is_erase_on_disable && !wasDisabled) {
				clearQuestionState(x, setNarValue, setRadioValue, setMultiValue, setNumValue);
			}
			if (typeof x.was_required === "undefined") x.was_required = x.is_required;
			x.is_required = false;
		} else if (x.was_required === true) {
			x.is_required = true;
		}
	};

	const isControlDisabled = disabled || !props.isModifiable;

	let captionComponent = (
		<>
			<Typography color={modified ? "primary" : ""} variant="subtitle1" display={"block"}>
				{x.caption + (x.is_required && !isControlDisabled ? "*" : "")}
			</Typography>
		</>
	);

	let answerElement = <></>;
	switch (x.details_type_code) {
		case "SINGLELIST":
			answerElement = (
				<RadioGroup
					sx={{ marginLeft: "20px" }}
					name={x.test_setup_details_id}
					value={radioValue}
					onChange={(_, val) => {
						setRadioValue(val);
						onAnswerChange(narValue, val, multiValue, numValue);
					}}>
					{x.Answers.map((y, idx) => {
						return (
							<FormControlLabel
								key={idx}
								value={y.test_setup_details_answers_id}
								disabled={isControlDisabled}
								label={y.test_setup_answers_caption}
								control={<Radio size="small"></Radio>}></FormControlLabel>
						);
					})}
				</RadioGroup>
			);
			break;
		case "NARRATIVE":
			answerElement = (
				<TextField
					value={narValue}
					disabled={isControlDisabled}
					sx={{ marginTop: "8px" }}
					fullWidth
					onChange={(event: any) => {
						const val = event?.target.value;
						setNarValue(val);
						onAnswerChange(val, radioValue, multiValue, numValue);
					}}
					rows={5}
					multiline
					label={x.Answers.length ? x.Answers[0].test_setup_answers_caption || x.caption : x.caption}></TextField>
			);
			if (!(x.Answers.length ? x.Answers[0].test_setup_answers_caption || x.caption : x.caption)) captionComponent = <></>;
			break;
		case "MULTILIST":
			answerElement = (
				<>
					{x.Answers.map((y, idx) => (
						<FormControlLabel
							sx={{ marginLeft: "8px" }}
							key={idx}
							style={{ display: "block" }}
							disabled={isControlDisabled}
							value={y.test_setup_details_answers_id}
							onChange={() => {
								const curVal = { ...multiValue };
								if (curVal.hasOwnProperty(y.test_setup_details_answers_id)) delete curVal[y.test_setup_details_answers_id];
								else curVal[y.test_setup_details_answers_id] = "";
								setMultiValue(curVal);
								onAnswerChange(narValue, radioValue, curVal, numValue);
							}}
							label={y.test_setup_answers_caption}
							checked={multiValue.hasOwnProperty(y.test_setup_details_answers_id)}
							control={<Checkbox size="small"></Checkbox>}></FormControlLabel>
					))}
				</>
			);
			break;
		case "NUMERIC_SCORE":
			answerElement = (
				<TextField
					type="number"
					disabled={isControlDisabled}
					sx={{ marginTop: "8px", display: "block", maxWidth: 240 }}
					size="small"
					fullWidth
					value={numValue}
					onChange={(event: any) => {
						const val = event?.target.value;
						setNumValue(val);
						onAnswerChange(narValue, radioValue, multiValue, val);
					}}></TextField>
			);
			break;
		default:
			console.log(x);
	}

	if (!isFormLineRenderedVisible(true, disabled, formClientOptions)) {
		return <></>;
	}

	return (
		<>
			{captionComponent}
			{answerElement}
		</>
	);
}
